import { logger } from '@adornis/base/logging.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { runOCROnImage } from '../api/agents/extraction-modules/ocr-utils.js';
import { OcrExtractionType } from '../api/agents/extraction-modules/ocrExtractionTypes.js';
import { TaskType } from '../api/task-types/task-main.js';
import { createLimiter } from './concurrency.js';
import { classifyTaskComparisonMode } from './copy-detection.js';
import { flattenExamTasks, flattenSubmissionTasks } from './submission-tree.js';

/** submissionId -> task path ("2.a") -> answer text. */
export type AnswerTextsBySubmission = Map<string, Map<string, string>>;

/**
 * Transcriptions made only for copy detection, keyed by the crop's image id. Kept out of the submission on purpose:
 * image-only task types (`forceImage`) are graded from the crop, and writing a transcription onto the submission
 * would change what grading and the review canvases show. A crop's pixels never change, so the cache never goes stale.
 */
export const TRANSCRIPT_COLLECTION = 'copy_detection_transcripts';

/** Module-scoped so concurrent copy-detection runs share one allowance of model calls. */
const ocrLimiter = createLimiter(4);

interface TranscriptDoc {
  _id: string;
  text: string;
  createdAt: Date;
}

function boxText(node: any): string {
  return (Array.isArray(node?.boundingBoxes) ? node.boundingBoxes : [])
    .map((box: any) => String(box?.ocrExtractedText ?? '').trim())
    .filter(Boolean)
    .join(' ');
}

function cropIds(node: any): string[] {
  return (Array.isArray(node?.boundingBoxes) ? node.boundingBoxes : [])
    .map((box: any) => box?.imageId)
    .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0);
}

/**
 * Resolves one comparable answer text per submission and answerable subtask: the stored answer, else the regions'
 * OCR text, else a transcription of the answer crops. The last case is every image-only task type, whose
 * `studentAnswer` is always empty — without it a maths exam has nothing to compare.
 */
export async function resolveAnswerTexts(
  exam: any,
  submissions: any[],
  signal?: AbortSignal,
): Promise<{ texts: AnswerTextsBySubmission; transcribedCount: number; cost: number }> {
  const answerablePaths = new Map<string, TaskType>();
  for (const { pathStr, node } of flattenExamTasks(exam)) {
    const taskType = node?.taskType as TaskType | undefined;
    if (!taskType || taskType === TaskType.TASK_FRAME || node?.needsAnswer === false) continue;
    if (classifyTaskComparisonMode(taskType) === 'unsupported') continue;
    answerablePaths.set(pathStr, taskType);
  }

  const texts: AnswerTextsBySubmission = new Map();
  const pending: { submissionId: string; pathStr: string; taskType: TaskType; cropIds: string[] }[] = [];

  for (const submission of submissions) {
    const submissionId = submission?._id ? String(submission._id) : null;
    if (!submissionId) continue;
    const byPath = new Map<string, string>();
    texts.set(submissionId, byPath);
    for (const { pathStr, node } of flattenSubmissionTasks(submission)) {
      const taskType = answerablePaths.get(pathStr);
      if (!taskType) continue;
      const stored = typeof node?.studentAnswer === 'string' ? node.studentAnswer.trim() : '';
      const text = stored || boxText(node);
      if (text) byPath.set(pathStr, text);
      else if (cropIds(node).length > 0) pending.push({ submissionId, pathStr, taskType, cropIds: cropIds(node) });
    }
  }

  const allCropIds = [...new Set(pending.flatMap(item => item.cropIds))];
  const collection = await getRawCollection(TRANSCRIPT_COLLECTION);
  const cached = (await collection.find({ _id: { $in: allCropIds } } as any).toArray()) as unknown as TranscriptDoc[];
  const textByCrop = new Map(cached.map(doc => [doc._id, doc.text]));

  let cost = 0;
  let transcribedCount = 0;
  const taskTypeByCrop = new Map(pending.flatMap(item => item.cropIds.map(id => [id, item.taskType] as const)));
  await Promise.all(
    allCropIds
      .filter(id => !textByCrop.has(id))
      .map(id =>
        ocrLimiter(async () => {
          if (signal?.aborted) return;
          const result = await runOCROnImage(id, OcrExtractionType.STUDENT_ANSWER, taskTypeByCrop.get(id) as string, c => {
            cost += c;
          });
          const text = String(result?.answer ?? '').trim();
          // A failed call returns confidence 0 — not cached, so the next run retries it.
          if (result?.confidence === 0 && !text) return;
          textByCrop.set(id, text);
          transcribedCount += 1;
          await collection.updateOne(
            { _id: id } as any,
            { $set: { text, createdAt: new Date() } },
            { upsert: true },
          );
        }).catch(err => logger.warn({ err, imageId: id }, 'Copy-detection transcription failed')),
      ),
  );

  for (const item of pending) {
    const text = item.cropIds
      .map(id => textByCrop.get(id) ?? '')
      .filter(Boolean)
      .join(' ');
    if (text) texts.get(item.submissionId)?.set(item.pathStr, text);
  }

  return { texts, transcribedCount, cost };
}
